import { canonicalGtin } from '@recipes/domain';
import { Alert, Button, Input, Label, cn } from '@recipes/ui';
import { Flashlight, FlashlightOff, Keyboard, ScanBarcode } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPlatform, type BarcodeDetectorLike } from '@/lib/platform';

type CameraState = 'idle' | 'starting' | 'running' | 'denied' | 'unavailable' | 'native';

const DETECT_INTERVAL_MS = 180;
const SAME_CODE_PAUSE_MS = 2500;

/**
 * Camera view with on-device barcode recognition (docs/finished/ingredient-catalog-spec.md 6.3). The
 * camera is asked for in context, the decoder comes from the platform layer, and every
 * function also works by typing the number. Nothing here touches the network.
 */
export function ScannerView({
  onCode,
  paused = false,
  compact = false,
}: {
  onCode: (barcode: string) => void;
  paused?: boolean;
  /** A camera window only as tall as the scan frame, the number field behind a toggle (phone sheet). */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const manualId = useId();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const lastRef = useRef<{ code: string; at: number }>({ code: '', at: 0 });
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;

  const [state, setState] = useState<CameraState>('idle');
  const [torch, setTorch] = useState<boolean | null>(null);
  const [manual, setManual] = useState('');
  const [manualError, setManualError] = useState(false);
  const [manualOpen, setManualOpen] = useState(!compact);

  // The camera keeps seeing the code it just read, so the same code is ignored for a moment.
  // A number the user typed is always meant.
  const emit = useCallback((raw: string, typed = false) => {
    const code = canonicalGtin(raw);
    if (!code) return false;
    const now = Date.now();
    if (!typed && lastRef.current.code === code && now - lastRef.current.at < SAME_CODE_PAUSE_MS)
      return true;
    lastRef.current = { code, at: now };
    navigator.vibrate?.(60);
    onCodeRef.current(code);
    return true;
  }, []);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const startStream = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setState('unavailable');
      return;
    }
    setState('starting');
    let detector: BarcodeDetectorLike;
    try {
      detector = await (await getPlatform()).createBarcodeDetector();
    } catch {
      // The decoder could not be loaded (first visit without a connection).
      setState('unavailable');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) return stop();
      video.srcObject = stream;
      await video.play().catch(() => undefined);
      const track = stream.getVideoTracks()[0];
      const capabilities = track?.getCapabilities?.() as { torch?: boolean } | undefined;
      setTorch(capabilities?.torch ? false : null);
      setState('running');

      let busy = false;
      const timer = window.setInterval(() => {
        if (busy || pausedRef.current || !streamRef.current || video.readyState < 2) return;
        busy = true;
        detector
          .detect(video)
          .then((codes) => codes.some((code) => emit(code)))
          .catch(() => undefined)
          .finally(() => {
            busy = false;
          });
      }, DETECT_INTERVAL_MS);
      stream.getVideoTracks()[0]?.addEventListener('ended', () => window.clearInterval(timer));
      (stream as MediaStream & { __timer?: number }).__timer = timer;
    } catch (error) {
      setState((error as DOMException)?.name === 'NotAllowedError' ? 'denied' : 'unavailable');
    }
  }, [emit, stop]);

  const scanNative = useCallback(async () => {
    const result = await (await getPlatform()).scanBarcodeNative();
    if (result === 'unsupported') return startStream();
    if (result) emit(result);
  }, [emit, startStream]);

  useEffect(() => {
    let cancelled = false;
    void getPlatform().then((platform) => {
      if (cancelled) return;
      if (platform.kind === 'native') setState('native');
      else void startStream();
    });
    return () => {
      cancelled = true;
      const timer = (streamRef.current as (MediaStream & { __timer?: number }) | null)?.__timer;
      if (timer) window.clearInterval(timer);
      stop();
    };
  }, [startStream, stop]);

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track || torch === null) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torch } as MediaTrackConstraintSet] });
      setTorch(!torch);
    } catch {
      setTorch(null);
    }
  };

  return (
    <div className="space-y-3">
      {state === 'native' ? (
        <Button
          type="button"
          size="lg"
          className="h-24 w-full"
          onClick={() => void scanNative()}
          data-testid="scanner-native"
        >
          <ScanBarcode aria-hidden /> {t('catalog.scanner.scan')}
        </Button>
      ) : (
        <div className="relative overflow-hidden rounded-lg border bg-scrim">
          <video
            ref={videoRef}
            muted
            playsInline
            className={compact ? 'h-28 w-full object-cover' : 'aspect-[4/3] w-full object-cover'}
            aria-label={t('catalog.scanner.camera')}
            data-testid="scanner-video"
          />
          {/* Frame that shows where to hold the code. */}
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-x-[12%] top-1/2 -translate-y-1/2 rounded-md border-2 border-on-scrim/80',
              compact ? 'h-16' : 'h-24',
            )}
          />
          {torch !== null && (
            <Button
              type="button"
              size="icon"
              variant="secondary"
              className="absolute right-2 top-2"
              aria-pressed={torch}
              aria-label={t('catalog.scanner.torch')}
              onClick={() => void toggleTorch()}
            >
              {torch ? <FlashlightOff aria-hidden /> : <Flashlight aria-hidden />}
            </Button>
          )}
          {state !== 'running' && (
            <div className="absolute inset-0 flex items-center justify-center bg-scrim/70 p-4 text-center text-sm text-on-scrim">
              {state === 'starting' && t('catalog.scanner.starting')}
              {state === 'denied' && t('catalog.scanner.denied')}
              {state === 'unavailable' && t('catalog.scanner.unavailable')}
              {state === 'idle' && t('catalog.scanner.starting')}
            </div>
          )}
        </div>
      )}
      {state === 'running' && !compact && (
        <p className="text-center text-xs text-muted-foreground" role="status">
          {paused ? t('catalog.scanner.paused') : t('catalog.scanner.hint')}
        </p>
      )}

      {compact && !manualOpen && (
        <button
          type="button"
          className="inline-flex min-h-[36px] items-center gap-1 text-xs text-muted-foreground underline"
          onClick={() => setManualOpen(true)}
          data-testid="scanner-manual-toggle"
        >
          <Keyboard className="size-3.5" aria-hidden /> {t('catalog.scanner.manual')}
        </button>
      )}
      <form
        className="flex items-end gap-2"
        hidden={!manualOpen}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const ok = emit(manual, true);
          setManualError(!ok);
          if (ok) setManual('');
        }}
      >
        <div className="flex-1">
          <Label htmlFor={manualId} className="flex items-center gap-1 text-xs">
            <Keyboard className="size-3.5" aria-hidden /> {t('catalog.scanner.manual')}
          </Label>
          <Input
            id={manualId}
            className="mt-1"
            inputMode="numeric"
            autoComplete="off"
            maxLength={16}
            value={manual}
            aria-invalid={manualError}
            onChange={(e) => {
              setManual(e.target.value.replace(/[^\d\s-]/g, ''));
              setManualError(false);
            }}
            data-testid="scanner-manual"
          />
        </div>
        <Button
          type="submit"
          variant="outline"
          disabled={!manual.trim()}
          data-testid="scanner-manual-submit"
        >
          {t('catalog.scanner.lookup')}
        </Button>
      </form>
      {manualError && (
        <Alert variant="destructive" data-testid="scanner-invalid">
          {t('catalog.scanner.invalid')}
        </Alert>
      )}
    </div>
  );
}
